import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { paymentsAPI } from "@/lib/api";
import { useAuth } from "@/App";
import { useTranslation } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Logo } from "@/components/shared/Logo";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentResult({ type }) {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (type === "cancel") { setStatus("cancelled"); return; }
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 5) { setStatus("timeout"); return; }
      try {
        const res = await paymentsAPI.getStatus(sessionId);
        if (res.data.payment_status === "paid") { setStatus("success"); await checkAuth(); return; }
        if (res.data.status === "expired") { setStatus("expired"); return; }
        attempts++;
        setTimeout(poll, 2000);
      } catch { attempts++; setTimeout(poll, 2000); }
    };
    poll();
  }, [sessionId, type, checkAuth]);

  const goBack = () => {
    if (user) { navigate(user.role === "hotel" ? "/hotel" : "/candidate", { replace: true }); }
    else { navigate("/", { replace: true }); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="payment-result-page">
      <Card className="max-w-md w-full bg-white shadow-xl">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="mb-6"><Logo size="md" className="text-slate-900 justify-center" /></div>
          {status === "checking" && (
            <>
              <Loader2 className="w-12 h-12 text-gold-500 mx-auto mb-4 animate-spin" />
              <h2 className="font-sans font-bold text-xl text-slate-900 mb-2">{t("pay.checking")}</h2>
              <p className="text-sm text-slate-500 font-body">{t("pay.wait")}</p>
            </>
          )}
          {status === "success" && (
            <>
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
              <h2 className="font-sans font-bold text-xl text-slate-900 mb-2">{t("pay.success")}</h2>
              <p className="text-sm text-slate-500 font-body mb-6">{t("pay.success_desc")}</p>
              <Button className="bg-gold-500 hover:bg-gold-600 text-white font-semibold rounded-sm" onClick={goBack} data-testid="payment-go-dashboard">
                {t("pay.go_dashboard")}
              </Button>
            </>
          )}
          {(status === "cancelled" || status === "expired") && (
            <>
              <XCircle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
              <h2 className="font-sans font-bold text-xl text-slate-900 mb-2">
                {status === "cancelled" ? t("pay.cancelled") : t("pay.expired")}
              </h2>
              <p className="text-sm text-slate-500 font-body mb-6">
                {status === "cancelled" ? t("pay.cancelled_desc") : t("pay.expired_desc")}
              </p>
              <Button className="bg-navy-900 hover:bg-navy-800 text-white font-semibold rounded-sm" onClick={goBack} data-testid="payment-go-back">
                {t("pay.back")}
              </Button>
            </>
          )}
          {(status === "error" || status === "timeout") && (
            <>
              <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
              <h2 className="font-sans font-bold text-xl text-slate-900 mb-2">{t("pay.error")}</h2>
              <p className="text-sm text-slate-500 font-body mb-6">{t("pay.error_desc")}</p>
              <Button className="bg-navy-900 hover:bg-navy-800 text-white font-semibold rounded-sm" onClick={goBack} data-testid="payment-error-back">
                {t("pay.back")}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
